import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, useState, ReactNode, lazy, Suspense } from "react";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import { AuthProvider } from "@/hooks/useAuth";

// Lazy load toast components to reduce unused JS on initial load
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Import Index directly for faster LCP (it's the landing page)
import Index from "./pages/Index";

// Lazy load other pages for code splitting
const Books = lazy(() => import("./pages/Books"));
const BookDetail = lazy(() => import("./pages/BookDetail"));
const Publishers = lazy(() => import("./pages/Publishers"));
const PublisherDetail = lazy(() => import("./pages/PublisherDetail"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const NewsletterUnsubscribe = lazy(() => import("./pages/NewsletterUnsubscribe"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));

// Cookie consent banner
import CookieConsent from "./components/CookieConsent";

import ScrollToTop from "./components/ScrollToTop";
import GoogleAnalytics from "./components/GoogleAnalytics";
const queryClient = new QueryClient();

// Page transition wrapper - starts visible to avoid white flash on initial load
const PageTransition = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    // Skip transition on first render to avoid flash
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    
    // Quick fade for route changes
    setIsVisible(false);
    const timeout = setTimeout(() => setIsVisible(true), 30);
    return () => clearTimeout(timeout);
  }, [location.pathname]);

  return (
    <div
      className={`transition-opacity duration-150 ease-out ${
        isVisible ? "opacity-100" : "opacity-95"
      }`}
    >
      {children}
    </div>
  );
};

// Suspense wrapper for lazy-loaded routes
const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const AppRoutes = () => (
  <>
    <GoogleAnalytics />
    <AnnouncementBanner />
    <ScrollToTop />
    <PageTransition>
      <main>
        <Routes>
          {/* Index is not lazy loaded for faster LCP */}
          <Route path="/" element={<Index />} />
          
          {/* Other routes are lazy loaded - Suspense wraps the element, not the Route */}
          <Route path="/books" element={<Suspense fallback={<LazyFallback />}><Books /></Suspense>} />
          <Route path="/books/:slug" element={<Suspense fallback={<LazyFallback />}><BookDetail /></Suspense>} />
          <Route path="/publishers" element={<Suspense fallback={<LazyFallback />}><Publishers /></Suspense>} />
          <Route path="/publishers/:slug" element={<Suspense fallback={<LazyFallback />}><PublisherDetail /></Suspense>} />
          <Route path="/about" element={<Suspense fallback={<LazyFallback />}><About /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<LazyFallback />}><Contact /></Suspense>} />
          <Route path="/auth" element={<Suspense fallback={<LazyFallback />}><Auth /></Suspense>} />
          <Route path="/admin" element={<Suspense fallback={<LazyFallback />}><Admin /></Suspense>} />
          <Route path="/newsletter/unsubscribe" element={<Suspense fallback={<LazyFallback />}><NewsletterUnsubscribe /></Suspense>} />
          
          {/* Legal pages */}
          <Route path="/legal" element={<Suspense fallback={<LazyFallback />}><LegalNotice /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<LazyFallback />}><PrivacyPolicy /></Suspense>} />
          <Route path="/cookies" element={<Suspense fallback={<LazyFallback />}><CookiePolicy /></Suspense>} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<Suspense fallback={<LazyFallback />}><NotFound /></Suspense>} />
        </Routes>
      </main>
    </PageTransition>
    <CookieConsent />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <AuthProvider>
        <TooltipProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
